import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import TrackCard from "../components/TrackCard";
import ShareModal from "../components/ShareModal";
import { Play, Share2, Trash2, Edit3, Globe, Lock, Save, ListMusic } from "lucide-react";
import { toast } from "sonner";

export const PlaylistPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { playTrack } = usePlayer();
  const navigate = useNavigate();
  const [pl, setPl] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", is_public: true });
  const [showShare, setShowShare] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/playlists/${id}`);
      setPl(data);
      setForm({ title: data.title, description: data.description || "", is_public: data.is_public });
    } catch { toast.error("Playlist introuvable"); navigate("/library"); }
  };

  useEffect(() => { load(); }, [id]);

  if (!pl) return <div className="p-12 text-white/60">Chargement...</div>;

  const isOwner = user?.user_id === pl.user_id;

  const save = async () => {
    try {
      await api.patch(`/playlists/${id}`, form);
      setEditing(false);
      load();
      toast.success("Mise à jour");
    } catch { toast.error("Erreur"); }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer cette playlist ?")) return;
    await api.delete(`/playlists/${id}`);
    toast.success("Supprimée");
    navigate("/library");
  };

  const removeTrack = async (tid) => {
    await api.delete(`/playlists/${id}/tracks/${tid}`);
    load();
  };

  const playAll = () => {
    if (!pl.tracks?.length) return;
    const first = pl.tracks[0];
    // Si c'est une vidéo externe (TikTok, YouTube, Instagram…) ou une vidéo uploadée,
    // on ouvre la page détail avec le contexte playlist pour naviguer.
    if (first.track_type === "video" || first.external_embed) {
      navigate(`/track/${first.track_id}?playlist=${id}`);
      return;
    }
    playTrack(first, pl.tracks);
  };

  return (
    <div className="px-4 md:px-12 py-10 max-w-7xl mx-auto pb-32" data-testid="playlist-page">
      <div className="flex flex-col md:flex-row md:items-end gap-6 mb-10">
        <div className="w-44 h-44 md:w-56 md:h-56 rounded-2xl bg-gradient-to-br from-brand-red via-brand-orange to-brand-yellow flex items-center justify-center shrink-0">
          <ListMusic size={72} className="text-white/80" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[0.25em] uppercase text-brand-orange font-bold mb-2">Playlist</p>
          {editing ? (
            <>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 font-display text-3xl font-black mb-2"
                data-testid="playlist-title-input"
              />
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm mb-2"
                placeholder="Description..."
                data-testid="playlist-desc-input"
              />
              <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer">
                <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} className="accent-brand-red" data-testid="playlist-public-checkbox" />
                Playlist publique
              </label>
            </>
          ) : (
            <>
              <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mb-2">{pl.title}</h1>
              {pl.description && <p className="text-white/70 mb-2">{pl.description}</p>}
              <p className="text-sm text-white/60 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1">{pl.is_public ? <><Globe size={12} /> Publique</> : <><Lock size={12} /> Privée</>}</span>
                <span>·</span>
                <span>Par <Link to={`/artist/${pl.user_id}`} className="text-brand-orange hover:text-brand-red">{pl.user_name}</Link></span>
                <span>·</span>
                <span>{pl.tracks?.length || 0} morceaux</span>
              </p>
            </>
          )}

          <div className="flex flex-wrap gap-2 mt-5">
            {pl.tracks?.length > 0 && (
              <button onClick={playAll} className="btn-primary inline-flex items-center gap-2" data-testid="playlist-play-all">
                <Play size={14} fill="currentColor" /> Tout lire
              </button>
            )}
            <button onClick={() => setShowShare(true)} className="btn-ghost inline-flex items-center gap-2" data-testid="playlist-share-btn">
              <Share2 size={14} /> Partager
            </button>
            {isOwner && !editing && (
              <>
                <button onClick={() => setEditing(true)} className="btn-ghost inline-flex items-center gap-2" data-testid="playlist-edit-btn">
                  <Edit3 size={14} /> Modifier
                </button>
                <button onClick={remove} className="btn-ghost inline-flex items-center gap-2 text-red-400" data-testid="playlist-delete-btn">
                  <Trash2 size={14} /> Supprimer
                </button>
              </>
            )}
            {isOwner && editing && (
              <>
                <button onClick={save} className="btn-primary inline-flex items-center gap-2" data-testid="playlist-save-btn">
                  <Save size={14} /> Enregistrer
                </button>
                <button onClick={() => { setEditing(false); load(); }} className="btn-ghost">Annuler</button>
              </>
            )}
          </div>
        </div>
      </div>

      {pl.tracks?.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
          <p className="text-white/60">Cette playlist est vide. Ajoutez-y des morceaux depuis n'importe quelle page !</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {pl.tracks.map((t) => (
            <div key={t.track_id} className="relative">
              <TrackCard track={t} list={pl.tracks} playlistId={id} />
              {isOwner && (
                <button
                  onClick={() => removeTrack(t.track_id)}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white hover:bg-red-500/80 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                  data-testid={`remove-from-playlist-${t.track_id}`}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <ShareModal open={showShare} onClose={() => setShowShare(false)} title={pl.title} path={`/playlist/${pl.playlist_id}`} />
    </div>
  );
};

export default PlaylistPage;
